import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { isEmpty, debounce } from 'lodash';
import { Grid } from 'antd';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { getSummaryScale } from '../../../../../core/utils/campaigns';
import { daysWeek } from '../../../../../core/utils/constants/constants';

/**
 * DualAxisAreaChart (v2, Recharts)
 *
 * Dual-axis stacked area chart — two metrics share an X axis (typically dates)
 * and each have their own Y axis. Drop-in replacement for the BizCharts
 * implementation duplicated across 10+ feature modules. Same props API:
 *
 *   <DualAxisAreaChart
 *     dualChartData={[{ xAxis, impressions, clicks, ... }, ...]}
 *     leftY="Impressions"
 *     rightY="Clicks"
 *   />
 *
 * Migration trajectory:
 *   - New features + phase-2 passes import from here
 *   - Legacy copies under `ux/<Feature>/.../DualAxisAreaChart.js` stay until
 *     each module's phase-2 swaps the import
 *   - Once zero BizCharts callers remain, remove the library dependency
 *
 * Visual parity with BizCharts original:
 *   - Colors identical (#b2d5ff / #89d2d4 fills; #7dafff / #92d5a7 axis labels)
 *   - 400px height, responsive width
 *   - Day-of-week X-axis label support (for the "Day Of Week" tab)
 *   - Axis formatters from getSummaryScale (currency, sig-fig, etc.)
 *
 * Recharts additions (better than v1):
 *   - Smooth gradient fills (v1 was flat fills)
 *   - Proper Legend component — self-documenting without relying on axis picker
 *   - Tooltip uses v2 design-system card styling (via CSS vars + .mjl-ds scoping)
 */

// Visual palette — locked to v1 BizCharts colors for cross-module consistency
// during the migration period. When ALL callers have moved to this component,
// these can be refactored to read from --sys / v2 tokens.
const LEFT_FILL = '#b2d5ff';
const LEFT_STROKE = '#7dafff';
const RIGHT_FILL = '#89d2d4';
const RIGHT_STROKE = '#92d5a7';
// Dashed "previous period" overlays — color-coded to their axis (a darker shade
// of the current-series hue) so the left and right comparisons stay legible.
const LEFT_COMPARE_STROKE = '#3f6fb0';
const RIGHT_COMPARE_STROKE = '#4e9e7a';

const DAY_SET = new Set([
  daysWeek.Sun, daysWeek.Sunday,
  daysWeek.Mon, daysWeek.Monday,
  daysWeek.Tue, daysWeek.Tuesday,
  daysWeek.Wed, daysWeek.Wednesday,
  daysWeek.Thu, daysWeek.Thursday,
  daysWeek.Fri, daysWeek.Friday,
  daysWeek.Sat, daysWeek.Saturday,
]);

const formatXaxis = (day) => {
  if (day === daysWeek.Sun || day === daysWeek.Sunday) return daysWeek.Sunday;
  if (day === daysWeek.Mon || day === daysWeek.Monday) return daysWeek.Monday;
  if (day === daysWeek.Tue || day === daysWeek.Tuesday) return daysWeek.Tuesday;
  if (day === daysWeek.Wed || day === daysWeek.Wednesday) return daysWeek.Wednesday;
  if (day === daysWeek.Thu || day === daysWeek.Thursday) return daysWeek.Thursday;
  if (day === daysWeek.Fri || day === daysWeek.Friday) return daysWeek.Friday;
  if (day === daysWeek.Sat || day === daysWeek.Saturday) return daysWeek.Saturday;
  return day;
};

// "Total Cost" → "totalCost"; single-word labels lowercased.
// Mirrors the v1 key-resolution logic exactly.
const labelToKey = (label) => {
  if (!label) return '';
  if (!label.includes(' ')) return label.toLowerCase();
  return `${label.charAt(0).toLowerCase()}${label.slice(1)}`.replace(/ /g, '');
};

const DualAxisAreaChart = ({
  dualChartData,
  leftY,
  rightY,
  // Optional: override the data keys (they default to labelToKey(label)). Lets
  // a caller show a friendly legend label while reading a different underlying
  // key, e.g. legend "Page Views" reading row.clicks.
  leftKey: leftKeyProp,
  rightKey: rightKeyProp,
  // Optional GA-style comparison overlays — dashed "previous period" lines,
  // color-coded per axis: { left?: { dataKey, name }, right?: { dataKey, name } }.
  // Each row carries the prev value under `dataKey` (+ optional `previousDate`
  // for the tooltip). Omit -> unchanged (all legacy callers).
  comparisonSeries,
}) => {
  const leftKey = leftKeyProp || labelToKey(leftY);
  const rightKey = rightKeyProp || labelToKey(rightY);
  const cmpLeft = comparisonSeries && comparisonSeries.left;
  const cmpRight = comparisonSeries && comparisonSeries.right;
  const cmpLeftKey = cmpLeft && cmpLeft.dataKey;
  const cmpRightKey = cmpRight && cmpRight.dataKey;
  const cmpLeftName = cmpLeft && cmpLeft.name;
  const cmpRightName = cmpRight && cmpRight.name;

  // AntD breakpoint hook drives responsive height. Matches the pattern used
  // in feature/display-campaign-ui-overhaul's BizCharts responsive fix.
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = screens.xs;
  const isTablet = !screens.xs && screens.md === false;
  const chartHeight = isMobile ? 260 : isTablet ? 320 : 400;

  // Window-level resize listener that bumps a key on each resize. Forces
  // Recharts to re-mount and recompute dimensions — more reliable than
  // ResizeObserver inside flex-column parents, which was the pattern the
  // display-campaign-ui-overhaul branch settled on for BizCharts (forceFit).
  const [resizeKey, setResizeKey] = useState(0);
  const handleResize = useCallback(
    debounce(() => setResizeKey((k) => k + 1), 150),
    []
  );
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // Reshape rows to flat {xAxis, left, right} — matches v1 transformation.
  // Single-row data: prepend a synthetic zero row with a blank xAxis so the
  // chart renders a rising ramp from 0 → value instead of a lonely dot in
  // the middle of the canvas. The blank xAxis means only the real day's
  // tick is labeled; we don't claim a specific prior date.
  const data = useMemo(() => {
    if (isEmpty(dualChartData)) return [];
    const reshaped = dualChartData.map((row) => ({
      xAxis: row.xAxis,
      left: row[leftKey] != null ? Number(row[leftKey]) : undefined,
      right: row[rightKey] != null ? Number(row[rightKey]) : undefined,
      ...(cmpLeftKey ? { prevLeft: row[cmpLeftKey] != null ? Number(row[cmpLeftKey]) : null } : {}),
      ...(cmpRightKey
        ? { prevRight: row[cmpRightKey] != null ? Number(row[cmpRightKey]) : null }
        : {}),
      ...(cmpLeftKey || cmpRightKey ? { previousDate: row.previousDate } : {}),
    }));
    if (reshaped.length === 1) {
      return [
        {
          xAxis: '',
          left: 0,
          right: 0,
          ...(cmpLeftKey ? { prevLeft: 0 } : {}),
          ...(cmpRightKey ? { prevRight: 0 } : {}),
        },
        reshaped[0],
      ];
    }
    return reshaped;
  }, [dualChartData, leftKey, rightKey, cmpLeftKey, cmpRightKey]);

  // Axis scale config from shared util — same source v1 used.
  const scale = useMemo(
    () => getSummaryScale(dualChartData, leftKey, rightKey),
    [dualChartData, leftKey, rightKey]
  );

  const leftFmt = scale && scale.left && scale.left.formatter
    ? scale.left.formatter
    : (v) => v;
  const rightFmt = scale && scale.right && scale.right.formatter
    ? scale.right.formatter
    : (v) => v;

  // Compact axis formatter — "1.2M", "450K", "1.5K" — avoids truncation of
  // wide numbers (e.g. "1,234,567.00") inside the Y-axis gutter on aggregate
  // views. Tooltips keep the full-precision formatter for readability.
  const compactAxis = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1)}B`;
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
    // Small values — let the source formatter handle decimals / currency / %
    return leftFmt(n);
  };
  const compactAxisRight = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v;
    const abs = Math.abs(n);
    if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(abs >= 10_000_000_000 ? 0 : 1)}B`;
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
    return rightFmt(n);
  };

  if (isEmpty(dualChartData)) return <div />;

  const hasRight = rightY !== 'None';

  // Wrapper pattern: outer div has the FIXED height; inner div is absolutely
  // positioned to inset:0 of the outer. This is the standard Recharts fix
  // for flex-column parents where ResponsiveContainer would otherwise fail
  // to shrink past the SVG's natural width (SVGs have intrinsic content size
  // that fights flex shrinking). The absolute inner always matches the
  // outer's computed width, so ResizeObserver catches every resize cleanly
  // and the chart tracks the window width in real time.
  return (
    <div
      className="campaign-chart__responsive-container"
      style={{ width: '100%', height: chartHeight, position: 'relative', minWidth: 0 }}
      key={`${resizeKey}-${chartHeight}`}
    >
      <div style={{ position: 'absolute', inset: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          // Right margin buffers the rightmost X-axis tick label, which
          // centers on the last data point and extends ~40-45px past it
          // for "YYYY-MM-DD" labels at 12px font. In dual-axis mode the
          // right YAxis (`width={64}`) already provides that buffer; in
          // single-axis mode there's no axis on the right, so we widen
          // the margin so the label sits inside `__chart-body`'s
          // `overflow: hidden` boundary. Pair with XAxis `padding` below
          // for a deterministic right-edge buffer.
          margin={{ top: 16, right: hasRight ? 16 : 48, bottom: 8, left: 0 }}
        >
          <defs>
            <linearGradient id="ds-dualaxis-left" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={LEFT_FILL} stopOpacity={0.55} />
              <stop offset="100%" stopColor={LEFT_FILL} stopOpacity={0.03} />
            </linearGradient>
            <linearGradient id="ds-dualaxis-right" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={RIGHT_FILL} stopOpacity={0.55} />
              <stop offset="100%" stopColor={RIGHT_FILL} stopOpacity={0.03} />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e8e8e8"
            vertical={false}
          />

          <XAxis
            dataKey="xAxis"
            tickFormatter={formatXaxis}
            stroke="#94a3b8"
            fontSize={isMobile ? 10 : 12}
            tickMargin={8}
            axisLine={{ stroke: '#e8e8e8' }}
            tickLine={false}
            minTickGap={20}
            // `padding.right` pushes the last data point ~12px inward in
            // single-axis mode so the centered tick label has room beyond
            // it without relying solely on outer margin. Dual-axis charts
            // get a YAxis on the right and don't need this padding.
            padding={hasRight ? undefined : { left: 4, right: 12 }}
          />

          <YAxis
            yAxisId="left"
            orientation="left"
            tickFormatter={compactAxis}
            stroke={LEFT_STROKE}
            fontSize={isMobile ? 10 : 12}
            tickMargin={8}
            axisLine={false}
            tickLine={false}
            width={64}
          />

          {hasRight && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={compactAxisRight}
              stroke={RIGHT_STROKE}
              fontSize={isMobile ? 10 : 12}
              tickMargin={8}
              axisLine={false}
              tickLine={false}
              width={64}
            />
          )}

          <Tooltip
            contentStyle={{
              background: '#fff',
              border: '1px solid #eeeeee',
              borderRadius: 12,
              boxShadow:
                '0 4px 12px rgba(0,0,0,0.06), 0 16px 40px rgba(0,0,0,0.10)',
              fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
              fontSize: 13,
              padding: 12,
            }}
            labelStyle={{
              color: '#111827',
              fontWeight: 600,
              marginBottom: 4,
              letterSpacing: '-0.01em',
            }}
            itemStyle={{ padding: 0 }}
            labelFormatter={(label, payload) => {
              const row = payload && payload[0] && payload[0].payload;
              if (row && row.previousDate) return `${formatXaxis(label)} · prev ${row.previousDate}`;
              return formatXaxis(label);
            }}
            formatter={(value, name) => {
              if (name === leftY) return [leftFmt(value), leftY];
              if (name === rightY) return [rightFmt(value), rightY];
              if (cmpLeftName && name === cmpLeftName) return [leftFmt(value), cmpLeftName];
              if (cmpRightName && name === cmpRightName) return [rightFmt(value), cmpRightName];
              return [value, name];
            }}
            cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '3 3' }}
          />

          <Legend
            verticalAlign="top"
            height={32}
            iconType="circle"
            iconSize={8}
            wrapperStyle={{
              fontSize: 12,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              color: '#64748b',
              paddingBottom: 8,
            }}
          />

          <Area
            yAxisId="left"
            type="monotone"
            dataKey="left"
            name={leftY}
            stroke={LEFT_STROKE}
            strokeWidth={2}
            fill="url(#ds-dualaxis-left)"
            animationDuration={900}
            animationEasing="ease-out"
            isAnimationActive
            dot={false}
            activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: LEFT_STROKE }}
          />

          {cmpLeftKey && (
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="prevLeft"
              name={cmpLeftName}
              stroke={LEFT_COMPARE_STROKE}
              strokeWidth={2}
              strokeDasharray="4 3"
              fill="none"
              connectNulls={false}
              animationDuration={900}
              animationEasing="ease-out"
              isAnimationActive
              dot={false}
              activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2, fill: LEFT_COMPARE_STROKE }}
            />
          )}

          {hasRight && (
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="right"
              name={rightY}
              stroke={RIGHT_STROKE}
              strokeWidth={2}
              fill="url(#ds-dualaxis-right)"
              animationDuration={900}
              animationEasing="ease-out"
              isAnimationActive
              dot={false}
              activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: RIGHT_STROKE }}
            />
          )}

          {hasRight && cmpRightKey && (
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="prevRight"
              name={cmpRightName}
              stroke={RIGHT_COMPARE_STROKE}
              strokeWidth={2}
              strokeDasharray="4 3"
              fill="none"
              connectNulls={false}
              animationDuration={900}
              animationEasing="ease-out"
              isAnimationActive
              dot={false}
              activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2, fill: RIGHT_COMPARE_STROKE }}
            />
          )}
        </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DualAxisAreaChart;
